import {
	ActionIcon,
	Avatar,
	Button,
	Card,
	Divider,
	Drawer,
	Flex,
	Menu,
	Modal,
	ScrollArea,
	SimpleGrid,
	Skeleton,
	Loader,
	Stack,
	Text,
	TextInput,
	Textarea,
	UnstyledButton,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
	IconDotsVertical,
	IconEdit,
	IconEye,
	IconFileText,
	IconMail,
	IconMessageCircle,
	IconNote,
	IconTrash,
	IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	getActiveUsers,
	sendActiveUserEmail,
} from 'src/apis/userActivityAPI'
import {
	CmsNote,
	createCmsNote,
	deleteCmsNote,
	getCmsNotes,
	updateCmsNote,
} from 'src/apis/cmsNoteAPI'
import {
	createSupportInboxCampaign,
	sendSupportInboxCampaign,
	streamSupportInboxCampaign,
} from 'src/apis/supportInboxAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import { useModal } from 'src/context/ModalContext'
import { color, formatDate, size } from 'src/variable/constant'

type ActivityCategory =
	| 'highly_active'
	| 'active'
	| 'potential'
	| 'at_risk'
	| 'dormant'

interface ActiveUser {
	id: number
	user_id: string
	active_days: number
	total_sessions: number
	category: ActivityCategory
	updated_at?: string | null
	user?: {
		id?: string
		name?: string | null
		phone?: string | null
		email?: string | null
		avatar?: string | null
		address?: string | null
		address_local?: string | null
		invite_code?: string | null
		version?: string | null
		blocked_at?: string | number | null
		unblocked_at?: string | number | null
		is_verified?: string | boolean | null
		is_regular?: string | boolean | null
		created_at?: string | null
		country?: string | null
		country_code?: string | null
		online_time?: string | number | null
	} | null
}

type ActiveUserRow = ActiveUser

type ActiveUserSearchFilter = {
	field: string
	value: string | null
}

const SUPPORT_INBOX_TARGET_SELECTED = 'SELECTED'
const SUPPORT_INBOX_TERMINAL_EVENTS = ['completed', 'failed', 'cancelled']

const ACTIVE_USER_SEARCH_FIELDS = [
	{
		value: 'name',
		label: 'Name',
	},
	{
		value: 'old_name',
		label: 'Old Name',
	},
	{
		value: 'email',
		label: 'Email',
	},
	{
		value: 'phone',
		label: 'Phone',
	},
	{
		value: 'invite_code',
		label: 'Invite code',
	},
]

const DEFAULT_ACTIVE_USER_SEARCH_FILTER: ActiveUserSearchFilter = {
	field: ACTIVE_USER_SEARCH_FIELDS[0].value,
	value: null,
}

const CATEGORY_OPTIONS: Array<{
	value: 'all' | ActivityCategory
	label: string
	description: string
	color: string
}> = [
	{
		value: 'all',
		label: 'All',
		description: 'All activity groups',
		color: '#1f7a44',
	},
	{
		value: 'highly_active',
		label: 'VIP Active',
		description: '≥ 20 days',
		color: '#238636',
	},
	{
		value: 'active',
		label: 'Active',
		description: '≥ 10 days',
		color: '#ca8a04',
	},
	{
		value: 'potential',
		label: 'Potential',
		description: '≥ 3 days',
		color: '#ea580c',
	},
	{
		value: 'at_risk',
		label: 'At Risk',
		description: '≥ 1 day',
		color: '#dc2626',
	},
	{
		value: 'dormant',
		label: 'Dormant',
		description: 'Does not meet another group',
		color: '#64748b',
	},
]

const formatNoteDate = (value: string) => {
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return '-'

	return new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	}).format(date)
}

const sortCmsNotes = (items: CmsNote[]) =>
	[...items].sort(
		(first, second) =>
			new Date(second.updated_at || second.created_at).getTime() -
			new Date(first.updated_at || first.created_at).getTime(),
	)

interface NotesPanelProps {
	user: ActiveUserRow
	notes: CmsNote[]
	loading: boolean
	value: string
	editingNoteId: string | null
	onValueChange: (value: string) => void
	onSave: () => void
	onEdit: (note: CmsNote) => void
	onDelete: (note: CmsNote) => void
	onClose: () => void
}

function NotesPanel({
	user,
	notes,
	loading,
	value,
	editingNoteId,
	onValueChange,
	onSave,
	onEdit,
	onDelete,
	onClose,
}: NotesPanelProps) {
	const userName = user.user?.name?.trim() || 'Unknown user'

	return (
		<Stack spacing={0} sx={{ height: '100%' }}>
			<Flex align="center" justify="space-between" gap={12} p="md">
				<Stack spacing={0}>
					<Text fw={700} size="lg">
						Notes for {userName}
					</Text>
					<Text size="xs" color="dimmed">
						Saved on this browser
					</Text>
				</Stack>
				<ActionIcon
					variant="subtle"
					color="gray"
					onClick={onClose}
					aria-label="Close notes"
				>
					<IconX size={20} />
				</ActionIcon>
			</Flex>

			<Divider />

			<ScrollArea sx={{ flex: 1 }}>
				<Stack p="md" spacing="lg">
					<Card
						withBorder
						radius="md"
						p="md"
						sx={{ background: '#f7faf7', borderColor: '#dce9df' }}
					>
						<Stack spacing="sm">
							<Flex align="center" gap={8}>
								<IconEdit size={17} color={color.CmsPrimary} />
								<Text fw={700} size="sm" color={color.CmsPrimary}>
									{editingNoteId ? 'Edit Note' : 'Create Note'}
								</Text>
							</Flex>
							<Textarea
								value={value}
								onChange={(event) => onValueChange(event.currentTarget.value)}
								placeholder="Write a note..."
								minRows={4}
								autosize
								maxRows={8}
								styles={{
									input: {
										borderColor: '#d8e4da',
										background: '#ffffff',
									},
								}}
							/>
							<Flex justify="flex-end">
								<Button
									onClick={onSave}
									disabled={!value.trim()}
									sx={{
										background: color.CmsPrimary,
										'&:hover': {
											background: `${color.CmsPrimary} !important`,
										},
									}}
								>
									Done
								</Button>
							</Flex>
						</Stack>
					</Card>

					<Stack spacing="sm">
						<Text fw={700}>Recent Notes</Text>
						{loading ? (
							<Card withBorder radius="md" p="lg">
								<Stack align="center" spacing={8}>
									<Loader size="sm" color="green" />
									<Text size="sm" color="dimmed">
										Loading notes...
									</Text>
								</Stack>
							</Card>
						) : notes.length === 0 ? (
							<Card withBorder radius="md" p="lg">
								<Stack align="center" spacing={6}>
									<IconFileText size={28} color="#94a3b8" />
									<Text size="sm" fw={600}>
										No notes yet
									</Text>
									<Text size="xs" color="dimmed" align="center">
										Create the first note for this user.
									</Text>
								</Stack>
							</Card>
						) : (
							notes.map((note) => (
								<Card key={note.id} withBorder radius="md" p="md">
									<Stack spacing={10}>
										<Flex justify="space-between" align="flex-start" gap={12}>
											<Stack spacing={2}>
												<Text size="xs" color="dimmed">
													{formatNoteDate(note.updated_at || note.created_at)}
												</Text>
												{note.updated_at !== note.created_at && (
													<Text size="xs" color="dimmed">
														Edited
													</Text>
												)}
											</Stack>
											<Menu shadow="md" width={140} withinPortal position="bottom-end">
												<Menu.Target>
													<ActionIcon
														variant="subtle"
														color="gray"
														aria-label="Note actions"
													>
														<IconDotsVertical size={17} />
													</ActionIcon>
												</Menu.Target>
												<Menu.Dropdown>
													<Menu.Item
														icon={<IconEdit size={15} />}
														onClick={() => onEdit(note)}
													>
														Edit
													</Menu.Item>
													<Menu.Item
														color="red"
														icon={<IconTrash size={15} />}
														onClick={() => onDelete(note)}
													>
														Delete
													</Menu.Item>
												</Menu.Dropdown>
											</Menu>
										</Flex>
										<Text
											size="sm"
											sx={{
												whiteSpace: 'pre-wrap',
												overflowWrap: 'anywhere',
												lineHeight: 1.55,
											}}
										>
											{note.content}
										</Text>
									</Stack>
								</Card>
							))
						)}
					</Stack>
				</Stack>
			</ScrollArea>
		</Stack>
	)
}

function ActiveUsersPage() {
	const navigate = useNavigate()
	const { openError, openModal } = useModal()
	const isMobileNotes = useMediaQuery('(max-width: 991px)')
	const [data, setData] = useState<ActiveUserRow[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [category, setCategory] = useState<'all' | ActivityCategory>('all')
	const [selectedUser, setSelectedUser] = useState<ActiveUserRow | null>(null)
	const [notes, setNotes] = useState<CmsNote[]>([])
	const [notesLoading, setNotesLoading] = useState(false)
	const [noteValue, setNoteValue] = useState('')
	const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
	const [emailUser, setEmailUser] = useState<ActiveUserRow | null>(null)
	const [emailForm, setEmailForm] = useState({ subject: '', body: '' })
	const [sendingEmail, setSendingEmail] = useState(false)
	const [messageUser, setMessageUser] = useState<ActiveUserRow | null>(null)
	const [messageBody, setMessageBody] = useState('')
	const [sendingMessage, setSendingMessage] = useState(false)
	const [messageProgress, setMessageProgress] = useState<any>(null)
	const [searchFilter, setSearchFilter] =
		useState<ActiveUserSearchFilter | null>(
			DEFAULT_ACTIVE_USER_SEARCH_FILTER,
		)
	const [debouncedSearchFilter, setDebouncedSearchFilter] =
		useState<ActiveUserSearchFilter | null>(searchFilter)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})

	const handleGetData = useCallback(async () => {
		setLoading(true)
		try {
			const offset = (pagination.pageIndex - 1) * pagination.pageSize
			const normalizedSearchFilter =
				debouncedSearchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER
			const res = await getActiveUsers({
				limit: pagination.pageSize,
				offset,
				...(category !== 'all' ? { category } : {}),
				...(normalizedSearchFilter.value?.trim()
					? {
							search_field:
								normalizedSearchFilter.field ||
								DEFAULT_ACTIVE_USER_SEARCH_FILTER.field,
							search_value: normalizedSearchFilter.value.trim(),
					  }
					: {}),
			})
			const objects = res?.data?.results?.objects
			const rows = Array.isArray(objects?.rows) ? objects.rows : []
			const count = Number(objects?.count) || 0

			setData(
				rows.map((item: ActiveUser) => ({
					...item,
					active_days: Number(item?.active_days) || 0,
					total_sessions: Number(item?.total_sessions) || 0,
				})),
			)
			setTotal(count)
		} catch (error) {
			setData([])
			setTotal(0)
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [
		category,
		debouncedSearchFilter,
		openError,
		pagination.pageIndex,
		pagination.pageSize,
	])

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				id: 'no',
				header: 'No',
				size: size.zero,
				Cell: ({ row }: any) =>
					(pagination.pageIndex - 1) * pagination.pageSize + row.index + 1,
			},
			{
				accessorKey: 'user.name',
				header: 'Name',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => {
					return (
						<CustomTooltip
							label={row.original.user?.name || '-'}
							maxWidth={size.text}
						/>
					)
				},
			},
			{
				accessorKey: 'user.avatar',
				header: 'Avatar',
				size: size.zero,
				Cell: ({ row }: any) => {
					return <Avatar src={row.original.user?.avatar || undefined} radius={50} />
				},
			},
			{
				accessorKey: 'active_days',
				header: 'Active',
				size: 145,
				Cell: ({ cell }: any) => (
					<Text fw={700} color={color.CmsPrimary}>
						{Number(cell.getValue() || 0).toLocaleString('en-US')}
					</Text>
				),
			},
			{
				accessorKey: 'total_sessions',
				header: 'Session',
				size: 140,
				Cell: ({ cell }: any) => (
					<Text fw={700} color={color.CmsPrimary}>
						{Number(cell.getValue() || 0).toLocaleString('en-US')}
					</Text>
				),
			},
			{
				accessorKey: 'user.address',
				header: 'GPS',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => {
					return (
						<CustomTooltip
							label={row.original.user?.address || '-'}
							maxWidth={size.medium}
						/>
					)
				},
			},
			{
				accessorKey: 'user.created_at',
				header: 'Date Join',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => {
					const createdAt = row.original.user?.created_at
					return createdAt ? dayjs(createdAt).format(formatDate) : '-'
				},
			},
			{
				accessorKey: 'user.email',
				header: 'Email',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => (
					<CustomTooltip
						label={row.original.user?.email || '-'}
						maxWidth={size.text}
					/>
				),
			},
			// Temporarily hidden: Phone, Address Local, Invite code, Version,
			// Status, Block expires, Verified, Last Active
		],
		[pagination.pageIndex, pagination.pageSize],
	)

	const handleCategoryChange = useCallback(
		(nextCategory: 'all' | ActivityCategory) => {
			setCategory(nextCategory)
			setPagination((previous) => ({
				...previous,
				pageIndex: 1,
			}))
		},
		[],
	)

	const handleChangePage = useCallback((pageIndex: number) => {
		setPagination((previous) => ({
			...previous,
			pageIndex,
		}))
	}, [])

	const handleSearchFilterChange = useCallback(
		(value: string | null, key: keyof ActiveUserSearchFilter) => {
			setSearchFilter((previous) => ({
				...(previous || DEFAULT_ACTIVE_USER_SEARCH_FILTER),
				[key]:
					key === 'field'
						? value || ACTIVE_USER_SEARCH_FIELDS[0].value
						: value,
			}))
			setPagination((previous) => ({
				...previous,
				pageIndex: 1,
			}))
		},
		[],
	)

	const handleChangePageSize = useCallback((pageSize: string | null) => {
		setPagination({
			pageIndex: 1,
			pageSize: Number(pageSize) || 10,
		})
	}, [])

	const handleDetail = useCallback((row: any) => {
		const userId = row?.original?.user?.id || row?.original?.user_id
		if (!userId) return

		navigate(`/users/${userId}`)
	}, [navigate])

	const loadNotes = useCallback(
		async (userId: string) => {
			setNotesLoading(true)
			try {
				const res = await getCmsNotes({
					limit: 100,
					offset: 0,
					target_type: 'active_user',
					target_id: userId,
					fields: '["$all"]',
				})
				const rows = res?.data?.results?.objects?.rows
				setNotes(Array.isArray(rows) ? sortCmsNotes(rows) : [])
			} catch (error) {
				setNotes([])
				openError(error)
			} finally {
				setNotesLoading(false)
			}
		},
		[openError],
	)

	const handleOpenNotes = useCallback((user: ActiveUserRow) => {
		setSelectedUser(user)
		setNotes([])
		setNoteValue('')
		setEditingNoteId(null)
		loadNotes(user.user_id)
	}, [loadNotes])

	const handleCloseNotes = useCallback(() => {
		setSelectedUser(null)
		setNotes([])
		setNoteValue('')
		setEditingNoteId(null)
	}, [])

	const handleSaveNote = useCallback(async () => {
		if (!selectedUser) return

		const content = noteValue.trim()
		if (!content) return

		setNotesLoading(true)
		try {
			if (editingNoteId) {
				const res = await updateCmsNote(editingNoteId, { content })
				const updatedNote = res?.data?.results?.object
				setNotes((previous) =>
					sortCmsNotes(
						previous.map((note) =>
							note.id === editingNoteId ? updatedNote || note : note,
						),
					),
				)
			} else {
				const res = await createCmsNote({
					content,
					target_type: 'active_user',
					target_id: selectedUser.user_id,
				})
				const createdNote = res?.data?.results?.object
				if (createdNote) {
					setNotes((previous) => sortCmsNotes([createdNote, ...previous]))
				}
			}
			setNoteValue('')
			setEditingNoteId(null)
		} catch (error) {
			openError(error)
		} finally {
			setNotesLoading(false)
		}
	}, [
		editingNoteId,
		noteValue,
		openError,
		selectedUser,
	])

	const handleEditNote = useCallback((note: CmsNote) => {
		setEditingNoteId(note.id)
		setNoteValue(note.content)
	}, [])

	const handleDeleteNote = useCallback(
		(note: CmsNote) => {
			if (!selectedUser) return

			openModal({
				type: 'confirm',
				label: 'Delete this note?',
				handleConfirm: async () => {
					try {
						await deleteCmsNote(note.id)
						setNotes((previous) =>
							previous.filter((item) => item.id !== note.id),
						)
					} catch (error) {
						openError(error)
						return
					}
					if (editingNoteId === note.id) {
						setEditingNoteId(null)
						setNoteValue('')
					}
				},
			})
		},
		[
			editingNoteId,
			openError,
			openModal,
			selectedUser,
		],
	)

	const handleOpenEmail = useCallback((user: ActiveUserRow) => {
		if (!user.user?.email) return

		setEmailUser(user)
		setEmailForm({ subject: '', body: '' })
	}, [])

	const handleCloseEmail = useCallback(() => {
		if (sendingEmail) return
		setEmailUser(null)
		setEmailForm({ subject: '', body: '' })
	}, [sendingEmail])

	const handleSendEmail = useCallback(async () => {
		if (!emailUser?.user_id) return

		const subject = emailForm.subject.trim()
		const body = emailForm.body.trim()
		if (!subject || !body) return

		setSendingEmail(true)
		try {
			await sendActiveUserEmail(emailUser.user_id, { subject, body })
			openModal({
				type: 'success',
				label: 'Email sent successfully',
			})
			setEmailUser(null)
			setEmailForm({ subject: '', body: '' })
		} catch (error) {
			openError(error)
		} finally {
			setSendingEmail(false)
		}
	}, [emailForm.body, emailForm.subject, emailUser, openError, openModal])

	const handleOpenMessage = useCallback((user: ActiveUserRow) => {
		if (!user.user_id) return

		setMessageUser(user)
		setMessageBody('')
		setMessageProgress(null)
	}, [])

	const handleCloseMessage = useCallback(() => {
		if (sendingMessage) return
		setMessageUser(null)
		setMessageBody('')
		setMessageProgress(null)
	}, [sendingMessage])

	const handleSendMessage = useCallback(async () => {
		if (!messageUser?.user_id) return

		const content = messageBody.trim()
		if (!content) return

		setSendingMessage(true)
		setMessageProgress(null)
		let terminalEvent = ''
		let terminalProgress: any = null
		try {
			const createRes = await createSupportInboxCampaign({
				content,
				target_type: SUPPORT_INBOX_TARGET_SELECTED,
				user_ids: [messageUser.user_id],
			})
			const campaign = createRes?.data?.results?.object
			await sendSupportInboxCampaign(campaign.id)
			setMessageProgress({
				id: campaign.id,
				status: 'QUEUED',
				total_count: campaign.total_count || 0,
				success_count: 0,
				failed_count: 0,
			})
			await streamSupportInboxCampaign(campaign.id, ({ event, data }) => {
				setMessageProgress(data)
				if (SUPPORT_INBOX_TERMINAL_EVENTS.includes(event)) {
					terminalEvent = event
					terminalProgress = data
				}
			})

			if (terminalEvent && terminalEvent !== 'completed') {
				throw new Error(
					terminalProgress?.error_message || 'Send message failed',
				)
			}

			openModal({
				type: 'success',
				label: 'Message sent successfully',
			})
			setMessageUser(null)
			setMessageBody('')
			setMessageProgress(null)
		} catch (error) {
			openError(error)
		} finally {
			setSendingMessage(false)
		}
	}, [messageBody, messageUser, openError, openModal])

	useEffect(() => {
		handleGetData()
	}, [handleGetData])

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchFilter(
				searchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER,
			)
		}, 500)

		return () => clearTimeout(timer)
	}, [searchFilter])

	const notesPanel = selectedUser ? (
		<NotesPanel
			user={selectedUser}
			notes={notes}
			loading={notesLoading}
			value={noteValue}
			editingNoteId={editingNoteId}
			onValueChange={setNoteValue}
			onSave={handleSaveNote}
			onEdit={handleEditNote}
			onDelete={handleDeleteNote}
			onClose={handleCloseNotes}
		/>
	) : null

	const messageStatusText = messageProgress
		? `Status: ${messageProgress.status || '-'} | Sent: ${
				messageProgress.success_count || 0
		  }/${messageProgress.total_count || 0} | Failed: ${
				messageProgress.failed_count || 0
		  }`
		: null

	return (
		<Stack sx={{ width: '100%' }} spacing="md">
			<Flex justify="space-between" align="flex-end" gap={16} wrap="wrap">
				<Stack spacing={4}>
					<Text fw={700} size="xl">
						Active Users
					</Text>
					<Text color="dimmed" size="sm">
						User activity based on active days and app sessions in the rolling
						30-day window
					</Text>
				</Stack>
				<CustomSelect
					label="Rows per page"
					value={String(pagination.pageSize)}
					onChange={handleChangePageSize}
					data={[
						{ value: '10', label: '10' },
						{ value: '20', label: '20' },
						{ value: '50', label: '50' },
					]}
					clearable={false}
					sx={{ minWidth: 130 }}
				/>
			</Flex>

			<SimpleGrid
				cols={6}
				spacing="sm"
				breakpoints={[
					{ maxWidth: 1280, cols: 3 },
					{ maxWidth: 768, cols: 2 },
					{ maxWidth: 480, cols: 1 },
				]}
			>
				{CATEGORY_OPTIONS.map((option) => {
					const selected = category === option.value

					return (
						<UnstyledButton
							key={option.value}
							onClick={() => handleCategoryChange(option.value)}
							aria-pressed={selected}
						>
							<Card
								withBorder
								radius="md"
								p="md"
								sx={{
									height: '100%',
									borderColor: selected ? option.color : '#e5e7eb',
									backgroundColor: selected ? `${option.color}0D` : '#ffffff',
									transition:
										'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
									'&:hover': {
										borderColor: option.color,
										transform: 'translateY(-1px)',
									},
								}}
							>
								<Stack spacing={6}>
									<Flex align="center" gap={8}>
										<span
											aria-hidden="true"
											style={{
												width: 9,
												height: 9,
												borderRadius: '50%',
												background: option.color,
												flexShrink: 0,
											}}
										/>
										<Text fw={700} size="sm">
											{option.label}
										</Text>
									</Flex>
									<Text size="xs" color="dimmed">
										{option.description}
									</Text>
								</Stack>
							</Card>
						</UnstyledButton>
					)
				})}
			</SimpleGrid>

			<Flex align="stretch" gap="md">
				<Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
					<Flex gap={8} sx={{ flexWrap: 'wrap', marginBottom: 16 }}>
						<CustomSelect
							value={
								(searchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER).field
							}
							data={ACTIVE_USER_SEARCH_FIELDS}
							onChange={(value) =>
								handleSearchFilterChange(value, 'field')
							}
						/>
						<CustomClearInput
							value={
								(searchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER).value
							}
							onChange={(value) =>
								handleSearchFilterChange(value, 'value')
							}
							placeholder="Search"
						/>
					</Flex>
					{loading && data.length === 0 ? (
						<Stack spacing={8}>
							{Array.from({ length: Math.min(pagination.pageSize, 10) }).map(
								(_, index) => (
									<Skeleton key={index} height={56} radius="md" />
								),
							)}
						</Stack>
					) : (
						<TableComponent
							columns={columns}
							total={total}
							data={data}
							pagination={pagination}
							loading={loading}
							handleChangePage={handleChangePage}
							enableRowActions
							hideMoreActions
							actionColumnSize={172}
							_renderCustomActions={(row: any) => (
								<Flex gap={6} align="center" wrap="nowrap">
									<CustomTooltip label="Send message">
										<span>
											<ActionIcon
												variant="light"
												color="green"
												disabled={!row.original.user_id}
												onClick={() => handleOpenMessage(row.original)}
												aria-label="Send message"
											>
												<IconMessageCircle size={17} />
											</ActionIcon>
										</span>
									</CustomTooltip>
									<CustomTooltip
										label={
											row.original.user?.email
												? 'Send email'
												: 'Email unavailable'
										}
									>
										<span>
											<ActionIcon
												variant="light"
												color="green"
												disabled={!row.original.user?.email}
												onClick={() => handleOpenEmail(row.original)}
												aria-label="Send email"
											>
												<IconMail size={17} />
											</ActionIcon>
										</span>
									</CustomTooltip>
									<CustomTooltip label="Notes">
										<ActionIcon
											variant="light"
											color="green"
											onClick={() => handleOpenNotes(row.original)}
											aria-label={`Open notes for ${
												row.original.user?.name || 'user'
											}`}
										>
											<IconNote size={17} />
										</ActionIcon>
									</CustomTooltip>
									<CustomTooltip label="Detail">
										<span>
											<ActionIcon
												variant="light"
												color="green"
												disabled={
													!(row.original.user?.id || row.original.user_id)
												}
												onClick={() => handleDetail(row)}
												aria-label={`Open detail for ${
													row.original.user?.name || 'user'
												}`}
											>
												<IconEye size={17} />
											</ActionIcon>
										</span>
									</CustomTooltip>
								</Flex>
							)}
							syncQueryUrl={false}
						/>
					)}
				</Stack>

				{selectedUser && !isMobileNotes && (
					<Card
						withBorder
						radius="md"
						p={0}
						sx={{
							width: 340,
							minWidth: 340,
							height: 'calc(100vh - 190px)',
							minHeight: 560,
							position: 'sticky',
							top: 16,
							overflow: 'hidden',
						}}
					>
						{notesPanel}
					</Card>
				)}
			</Flex>

			<Drawer
				opened={Boolean(selectedUser && isMobileNotes)}
				onClose={handleCloseNotes}
				position="right"
				size="100%"
				withCloseButton={false}
				padding={0}
			>
				{isMobileNotes ? notesPanel : null}
			</Drawer>

			<Modal
				opened={Boolean(messageUser)}
				onClose={handleCloseMessage}
				title="Send Message"
				size="lg"
				closeOnClickOutside={!sendingMessage}
				closeOnEscape={!sendingMessage}
			>
				<Stack>
					<Stack spacing={2}>
						<Text size="sm" color="dimmed">
							To: {messageUser?.user?.name || 'Unknown user'}
						</Text>
						<Text size="xs" color="dimmed">
							{messageUser?.user?.phone || messageUser?.user?.email || '-'}
						</Text>
					</Stack>
					<Textarea
						label="Message"
						minRows={8}
						value={messageBody}
						onChange={(event) => setMessageBody(event.currentTarget.value)}
						disabled={sendingMessage}
					/>
					{messageStatusText && (
						<Text size="sm" color="dimmed">
							{messageStatusText}
						</Text>
					)}
					<Flex justify="flex-end" gap={8}>
						<Button
							variant="default"
							onClick={handleCloseMessage}
							disabled={sendingMessage}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSendMessage}
							loading={sendingMessage}
							disabled={!messageBody.trim()}
							sx={{
								background: color.CmsPrimary,
								'&:hover': {
									background: `${color.CmsPrimary} !important`,
								},
							}}
						>
							Send
						</Button>
					</Flex>
				</Stack>
			</Modal>

			<Modal
				opened={Boolean(emailUser)}
				onClose={handleCloseEmail}
				title="Send Email"
				size="lg"
				closeOnClickOutside={!sendingEmail}
				closeOnEscape={!sendingEmail}
			>
				<Stack>
					<Text size="sm" color="dimmed">
						To: {emailUser?.user?.email || '-'}
					</Text>
					<TextInput
						label="Subject"
						value={emailForm.subject}
						onChange={(event) => {
							const subject = event.currentTarget.value
							setEmailForm((previous) => ({
								...previous,
								subject,
							}))
						}}
						disabled={sendingEmail}
					/>
					<Textarea
						label="Body"
						minRows={8}
						value={emailForm.body}
						onChange={(event) => {
							const body = event.currentTarget.value
							setEmailForm((previous) => ({
								...previous,
								body,
							}))
						}}
						disabled={sendingEmail}
					/>
					<Flex justify="flex-end" gap={8}>
						<Button
							variant="default"
							onClick={handleCloseEmail}
							disabled={sendingEmail}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSendEmail}
							loading={sendingEmail}
							disabled={
								!emailForm.subject.trim() || !emailForm.body.trim()
							}
							sx={{
								background: color.CmsPrimary,
								'&:hover': {
									background: `${color.CmsPrimary} !important`,
								},
							}}
						>
							Send
						</Button>
					</Flex>
				</Stack>
			</Modal>
		</Stack>
	)
}

export default React.memo(ActiveUsersPage)
